import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, CircularProgress, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { AxiosResponse } from 'axios';

import axios from '../../../sharedModules/utils/axios.utils';
import type { Media } from '../../../sharedModules/imagePreview/media';
import MediaUploadSection from '../../../sharedModules/mediaUpload/MediaUploadSection';
import { ReceivingDetail, ReceivingLineItem, ReceivingMedia, postReceivingPhotos } from '../model';

interface ReceivingItemsSectionProps {
  poNumber: string;
  location: string;
  // Bumped by the page after a scan, so the section reloads what the scan just wrote.
  reloadToken: number;
  onError: (message: string) => void;
  // The page gates its Submit button on a document photo existing, and the media list lives here.
  onDocumentPhotoChange: (hasDocumentPhoto: boolean) => void;
  // The page gates its Submit button on a package photo existing too, and the media list
  // lives here.
  onPackagePhotoChange: (hasPackagePhoto: boolean) => void;
  // A PO number that came back from a successful lookup. Everything downstream of the PO is
  // disabled until then - the packing slip section is the one exception, since it is the
  // input the scan reads the PO from.
  poValidated: boolean;
  // Likewise: a submit does not carry line items, so the page must not finalize over unsaved edits.
  onDirtyChange: (dirty: boolean) => void;
  // A write in this section is still in flight. The page folds it into its own busy state so a
  // submit cannot stitch the receipt before the photos it is waiting on have attached.
  onBusyChange: (busy: boolean) => void;
  // Pages photographed but not yet scanned. They live on the page (which owns the scan) and are
  // shown here so staged and already-scanned paperwork sit in one document area.
  stagedDocuments: File[];
  onStagedDocumentsChange: (files: File[]) => void;
}

// The two kinds this section uploads itself. The packing slip is staged on the page instead,
// because it is what the scan reads the PO from.
type PhotoKind = 'package' | 'equipment';

const EMPTY_LINE: ReceivingLineItem = { description: '', quantity: '' };

const toGalleryMedia = (item: ReceivingMedia): Media => ({
  id: item.id,
  url: item.url,
  content_type: item.content_type,
});

const ReceivingItemsSection: FC<ReceivingItemsSectionProps> = ({
  poNumber,
  location,
  reloadToken,
  onError,
  onDocumentPhotoChange,
  onPackagePhotoChange,
  poValidated,
  onDirtyChange,
  onBusyChange,
  stagedDocuments,
  onStagedDocumentsChange,
}) => {
  const [lineItems, setLineItems] = useState<ReceivingLineItem[]>([{ ...EMPTY_LINE }]);
  const [media, setMedia] = useState<ReceivingMedia[]>([]);
  const [hasScan, setHasScan] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // One entry per batch in flight, not a flag: a receiver can start a second batch before the
  // first resolves, and a boolean would let the first response clear busy while the other upload
  // is still going. Entries carry their kind so each section reports only its own progress.
  const [uploads, setUploads] = useState<PhotoKind[]>([]);

  // Every write here resolves after an await, by which time the receiver may have moved to another
  // PO or submitted and reset the page. Read at apply time to decide whether the response still
  // belongs to what is on screen; a ref rather than state so applyDetail needs no dependencies.
  const poNumberRef = useRef<string>(poNumber);
  poNumberRef.current = poNumber;

  const documentPhotos = useMemo(() => media.filter((item) => item.kind === 'document').map(toGalleryMedia), [media]);
  const packagePhotos = useMemo(() => media.filter((item) => item.kind === 'package').map(toGalleryMedia), [media]);
  // Matched explicitly rather than as "not a document": the old predicate would have swallowed
  // every package photo into the equipment gallery the moment the new kind appeared.
  const equipmentPhotos = useMemo(() => media.filter((item) => item.kind === 'equipment').map(toGalleryMedia), [media]);

  useEffect(() => {
    onDocumentPhotoChange(documentPhotos.length > 0);
  }, [documentPhotos, onDocumentPhotoChange]);

  useEffect(() => {
    onPackagePhotoChange(packagePhotos.length > 0);
  }, [packagePhotos, onPackagePhotoChange]);

  // Drop the previous PO's media the moment the number changes: until its load resolves it would
  // otherwise show that PO's paperwork under this number, and vouch for a document this PO may
  // not have. Keyed on poNumber alone so a post-scan reloadToken refresh does not blank the gallery.
  useEffect(() => {
    setMedia([]);
  }, [poNumber]);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onBusyChange(saving || uploads.length > 0);
  }, [saving, uploads, onBusyChange]);

  // requestedFor is the PO this response was asked for. A response for any other PO is dropped:
  // applying it would show one PO's paperwork and items under another's number, or repopulate the
  // form that a finished submit just cleared.
  const applyDetail = useCallback((detail: ReceivingDetail, requestedFor: string) => {
    if (requestedFor !== poNumberRef.current) return;

    setLineItems(detail.line_items?.length ? detail.line_items : [{ ...EMPTY_LINE }]);
    setMedia(detail.media || []);
    // The review warning is only honest when a scan produced the table. Telling a receiver their
    // own typing might have been misread is worse than saying nothing.
    setHasScan((detail.extracted_line_items || []).length > 0);
    setDirty(false);
  }, []);

  const clearSection = useCallback(() => {
    setLineItems([{ ...EMPTY_LINE }]);
    setMedia([]);
    setHasScan(false);
    setDirty(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!poNumber) return;

    // Gated on the lookup, not on the PO's shape, so one predicate governs loading and editing
    // alike. Two predicates diverged in both directions: a PO the lookup accepts but the format
    // rejects unlocked every control while never loading the receiving - leaving Submit
    // permanently disabled, since the flags it reads come from the media this fetch returns.
    if (!poValidated) {
      clearSection();
      return;
    }

    const controller = new AbortController();
    let stale = false;

    const load = async () => {
      setLoading(true);

      try {
        const response = await axios.get<ReceivingDetail>('/warehouse/receiving/photo_sets', {
          params: { po_number: poNumber, location },
          signal: controller.signal,
        });
        if (stale) return;
        applyDetail(response.data, poNumber);
      } catch (error: any) {
        if (
          stale ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError'
        ) {
          return;
        }
        onError(error?.response?.data?.error || 'Something went wrong loading this PO.');
      } finally {
        if (!stale) setLoading(false);
      }
    };

    load();

    return () => {
      stale = true;
      controller.abort();
    };
  }, [poNumber, poValidated, location, reloadToken, applyDetail, onError, clearSection]);

  const updateLine = (index: number, patch: Partial<ReceivingLineItem>) => {
    setLineItems((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
    setDirty(true);
  };

  const addLine = () => {
    setLineItems((current) => [...current, { ...EMPTY_LINE }]);
    setDirty(true);
  };

  const removeLine = (index: number) => {
    setLineItems((current) => current.filter((_, i) => i !== index));
    setDirty(true);
  };

  const saveLineItems = async () => {
    const requestedFor = poNumber;
    setSaving(true);

    try {
      const response = await axios.put<unknown, AxiosResponse<ReceivingDetail>>('/warehouse/receiving/line_items', {
        po_number: poNumber,
        location,
        line_items: lineItems.map((line) => ({
          description: line.description,
          quantity: line.quantity === '' ? null : line.quantity,
        })),
      });
      applyDetail(response.data, requestedFor);
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong saving these items.');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async (files: File[], kind: PhotoKind) => {
    if (files.length === 0) return;
    if (!poValidated) {
      onError('Look up a PO number before adding photos.');
      return;
    }

    const requestedFor = poNumber;
    setUploads((current) => [...current, kind]);

    try {
      const response = await postReceivingPhotos(kind, files, poNumber, location);
      applyDetail(response.data, requestedFor);
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong uploading those photos.');
    } finally {
      // Drops one entry for this kind, not every entry: a second batch of the same kind may
      // still be in flight.
      setUploads((current) => current.filter((_, index) => index !== current.indexOf(kind)));
    }
  };

  const removePhoto = async (id: number | string) => {
    try {
      await axios.delete(`/warehouse/receiving/photo_set_attachments/${id}`);
      setMedia((current) => current.filter((item) => item.id !== id));
    } catch (error: any) {
      onError(error?.response?.data?.error || 'Something went wrong deleting that photo.');
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
        Listed Items
      </Typography>

      {hasScan && (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          <strong>Read automatically from the photo &mdash; check it.</strong> Descriptions and quantities can be
          misread. Compare every line against the packing slip and correct anything wrong before saving.
        </Alert>
      )}

      {loading && <CircularProgress size={20} />}

      {lineItems.map((line, index) => (
        // Index-keyed deliberately: a new row has no id yet, and rows are only ever appended or
        // removed as a whole, never reordered, so the index is stable for a row's lifetime.
        // eslint-disable-next-line react/no-array-index-key
        <Grid container spacing={1} alignItems="center" key={index} style={{ marginBottom: 8 }}>
          <Grid item xs={8} sm={9}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={line.description}
              disabled={!poValidated}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateLine(index, { description: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={3} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="Qty"
              value={line.quantity ?? ''}
              disabled={!poValidated}
              inputProps={{ inputMode: 'decimal' }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateLine(index, { quantity: event.target.value })}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton aria-label="Remove item" disabled={!poValidated} onClick={() => removeLine(index)}>
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Stack direction="row" spacing={1} alignItems="center" style={{ marginTop: 8 }}>
        <Button size="small" startIcon={<Add />} disabled={!poValidated} onClick={addLine}>
          Add Item
        </Button>
        <Button size="small" variant="contained" onClick={saveLineItems} disabled={!poValidated || !dirty || saving}>
          {saving ? <CircularProgress size={18} /> : 'Save Items'}
        </Button>
        {!poValidated && (
          <Typography variant="caption" color="text.secondary">
            Look up a PO number to save these items.
          </Typography>
        )}
        {poValidated && dirty && !saving && (
          <Typography variant="caption" color="warning.main">
            Unsaved changes
          </Typography>
        )}
      </Stack>

      <Grid container spacing={2} style={{ marginTop: 16 }}>
        <Grid item xs={12} md={4}>
          <MediaUploadSection
            label={stagedDocuments.length > 0 ? 'Packing slip photos (not scanned yet)' : 'Packing slip photos'}
            files={stagedDocuments}
            onChange={onStagedDocumentsChange}
            existingMedia={documentPhotos}
            onRemoveExisting={removePhoto}
            allowVideo={false}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MediaUploadSection
            label={uploads.includes('package') ? 'Package photos (uploading...)' : 'Package photos'}
            files={[]}
            onChange={(files) => uploadPhotos(files, 'package')}
            existingMedia={packagePhotos}
            onRemoveExisting={removePhoto}
            allowVideo={false}
            disabled={!poValidated}
            required
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MediaUploadSection
            label={uploads.includes('equipment') ? 'Equipment photos (uploading...)' : 'Equipment photos'}
            files={[]}
            onChange={(files) => uploadPhotos(files, 'equipment')}
            existingMedia={equipmentPhotos}
            onRemoveExisting={removePhoto}
            allowVideo={false}
            disabled={!poValidated}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ReceivingItemsSection;
